import { ArrowDown, Flame, Truck } from "lucide-react";
import { WHATSAPP_LINK, ASSETS } from "../data/menu";

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-32 sm:pt-40 lg:pt-44 pb-20 sm:pb-28 lg:pb-32 overflow-hidden"
    >
      {/* Storefront background image with warm beige overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={ASSETS.storefront}
          alt="Local de Pizzería Estilo Gali"
          className="w-full h-full object-cover"
        />
        {/* Beige/nude warm overlay so foreground stays readable */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(244,239,230,0.72) 0%, rgba(244,239,230,0.55) 45%, rgba(244,239,230,0.92) 100%)",
          }}
        />
      </div>

      {/* Soft decorative blobs */}
      <div className="absolute -top-32 -right-20 w-[480px] h-[480px] rounded-full bg-[#EAE3D5] blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute top-1/3 -left-20 w-[300px] h-[300px] rounded-full bg-[#D96C42]/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Text content */}
          <div className="lg:col-span-7 order-2 lg:order-1 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F5EBD3] border border-[#E6DFD3]">
              <Flame size={14} strokeWidth={1.5} className="text-[#D96C42]" />
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5146]">
                Pizzas artesanales
              </span>
            </div>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tight leading-[0.95] font-medium text-[#2B241D] mt-8">
              El sabor de
              <br />
              casa, horneado
              <br />
              <em className="italic text-[#D96C42] font-normal">al horno.</em>
            </h1>

            <p className="font-sans text-base sm:text-lg text-[#2B241D]/80 leading-relaxed font-light mt-8 max-w-xl">
              En Pizzería Estilo Gali llevamos más que masa al horno: llevamos
              tiempo, fermentación lenta e ingredientes elegidos uno por uno.
              Pizzas pensadas para compartir entre los nuestros.
            </p>

            {/* Delivery badge */}
            <div
              data-testid="hero-delivery-badge"
              className="mt-6 inline-flex items-center gap-2.5 bg-[#F5EBD3] border border-[#E6DFD3] rounded-full pl-3 pr-5 py-2"
            >
              <span className="w-7 h-7 rounded-full bg-[#D96C42] text-white flex items-center justify-center">
                <Truck size={14} strokeWidth={2} />
              </span>
              <span className="font-sans text-sm text-[#2B241D]">
                Envíos del 20 hasta Tetitlán
              </span>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="#menu"
                data-testid="hero-cta-menu"
                className="group inline-flex items-center gap-3 bg-[#2B241D] text-[#F5EBD3] px-8 py-4 rounded-full text-sm font-medium hover:bg-[#D96C42] transition-colors"
              >
                Ver el menú
                <ArrowDown
                  size={16}
                  strokeWidth={1.5}
                  className="group-hover:translate-y-1 transition-transform"
                />
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="inline-flex items-center gap-3 text-[#2B241D] px-2 py-2 rounded-full text-sm font-medium border-b border-[#2B241D] hover:border-[#D96C42] hover:text-[#D96C42] transition-colors"
              >
                Pedir por WhatsApp
              </a>
            </div>

            {/* Quick stats */}
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="font-serif text-3xl sm:text-4xl text-[#2B241D]">
                  48h
                </div>
                <div className="font-sans text-xs uppercase tracking-[0.15em] text-[#5C5146] mt-2">
                  Fermentación
                </div>
              </div>
              <div>
                <div className="font-serif text-3xl sm:text-4xl text-[#2B241D]">
                  100%
                </div>
                <div className="font-sans text-xs uppercase tracking-[0.15em] text-[#5C5146] mt-2">
                  Masa crujiente
                </div>
              </div>
              <div>
                <div className="font-serif text-3xl sm:text-4xl text-[#2B241D]">
                  9hrs
                </div>
                <div className="font-sans text-xs uppercase tracking-[0.15em] text-[#5C5146] mt-2">
                  Abiertos diario
                </div>
              </div>
            </div>
          </div>

          {/* Logo card */}
          <div className="lg:col-span-5 order-1 lg:order-2 fade-up">
            <div className="relative">
              <div className="rounded-2xl sm:rounded-[2rem] overflow-hidden shadow-2xl shadow-[#2B241D]/15 bg-[#F5EBD3] aspect-square p-8 sm:p-12 flex items-center justify-center border border-[#E6DFD3]">
                <img
                  src={ASSETS.logo}
                  alt="Logo Pizzería Estilo Gali"
                  className="w-full h-full object-contain"
                  data-testid="hero-logo"
                />
              </div>
              {/* Badge card overlay */}
              <div className="hidden sm:block absolute -bottom-6 -left-6 bg-[#2B241D] text-[#F5EBD3] rounded-2xl p-5 shadow-xl shadow-[#2B241D]/20 max-w-[220px] float-y">
                <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#D96C42]">
                  Horario
                </div>
                <div className="font-serif text-2xl mt-1 leading-tight">
                  2:00 PM – 11:00 PM
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
